import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Globe, ExternalLink, Eye, Save, Link2, AppWindow, Inbox, KanbanSquare, ReceiptText, ArrowRight, ScrollText, Clock, Pencil, X, Trash2, AlertTriangle, Archive, ArchiveRestore, BarChart3, Users, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner, StatusBadge, Avatar, Empty, chf } from "@/components/common";
import UserManager from "@/components/UserManager";
import DocumentsManager from "@/components/DocumentsManager";
import WebsiteConnEditor from "@/components/WebsiteConnEditor";
import ReviewsTab from "./client-tabs/ReviewsTab";
import DesignLibraryTab from "./client-tabs/DesignLibraryTab";
import ContactsTab from "./client-tabs/ContactsTab";
import StammdatenCard from "./client-tabs/StammdatenCard";
import SpecialAppEditor from "./client-tabs/SpecialAppEditor";
import { canWriteClient } from "../../lib/clientPermissions";

const TABS = ["overview", "firma", "kontakte", "users", "website", "abo", "aufgaben", "leads", "bewertungen", "design", "reports", "invoices", "documents", "audit"];
const TAB_ALIASES = { tasks: "aufgaben", protokoll: "audit" };
const resolveTab = value => {
  const tab = TAB_ALIASES[value] || value;
  return TABS.includes(tab) ? tab : "overview";
};
const TAB_LABELS = {
  overview: "Übersicht", firma: "Firmendaten", kontakte: "Kontaktpersonen", users: "Benutzer", website: "Website & Technik",
  abo: "Abo & Vertrag", aufgaben: "Aufgaben & Änderungen", leads: "Anfragen / Leads", bewertungen: "Bewertungen",
  design: "Design- & Growth-Bibliothek", reports: "Monatsreport", invoices: "Rechnungen", documents: "Dokumente", audit: "Audit-Log",
};

function ClientUserAccess({ clientId, readOnly }) {
  return (
    <Card className="p-6" data-testid="detail-users">
      <h3 className="font-bold mb-1 flex items-center gap-2"><Users className="w-4 h-4 text-[#A3E635]" /> Cockpit-Zugänge</h3>
      <p className="text-xs text-gray-500 mb-4">Personen mit Login zum Kunden-Cockpit. Jede Person sieht ausschliesslich die Daten dieses Kunden. Getrennt von den Kontaktpersonen.</p>
      {readOnly ? (
        <p className="text-sm text-gray-400" data-testid="detail-users-readonly">Die Cockpit-Zugänge verwalten Admin / Operations (im Kundenscope) oder ein Super Admin.</p>
      ) : <UserManager clientId={clientId} accent="#A3E635" />}
    </Card>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [c, setC] = useState(null);
  const tab = resolveTab(searchParams.get("tab"));
  const setTab = value => setSearchParams(previous => {
    const next = new URLSearchParams(previous);
    next.set("tab", resolveTab(value));
    return next;
  }, { replace: true });
  const [allModules, setAllModules] = useState([]);
  const [packages, setPackages] = useState([]);
  const [checking, setChecking] = useState(false);
  const [delConfirm, setDelConfirm] = useState(null); // null = closed, "" = open
  const [delBusy, setDelBusy] = useState(false);

  const load = () => api.get(`/clients/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); api.get("/modules").then((r) => setAllModules(r.data)); api.get("/packages").then((r) => setPackages(r.data)); /* eslint-disable-next-line */ }, [id]);
  if (!c) return <Spinner />;

  const toggleModule = async (mid, active) => {
    await api.post(`/clients/${id}/modules`, { module_id: mid, active });
    toast.success(active ? "Modul aktiviert" : "Modul deaktiviert");
    load();
  };
  const checkSite = async () => {
    setChecking(true);
    try {
      await api.post(`/websites/${id}/check`);
      toast.success("Website geprüft");
      load();
    } catch { toast.error("Prüfung fehlgeschlagen"); } finally { setChecking(false); }
  };
  const setArchived = async (archived) => {
    try {
      await api.put(`/clients/${id}`, { status: archived ? "archiviert" : "aktiv" });
      toast.success(archived ? "Kunde archiviert" : "Kunde reaktiviert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };
  const setClientType = async (ct) => {
    try {
      await api.put(`/clients/${id}`, { client_type: ct });
      toast.success(ct === "paying" ? "Als zahlender Kunde gesetzt" : ct === "internal" ? "Als internes KEWAG-System gesetzt" : "Als Testsystem gesetzt");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };
  const deleteClient = async () => {
    setDelBusy(true);
    try {
      const { data } = await api.delete(`/clients/${id}`, { data: { confirm: delConfirm } });
      toast.success(`Kunde «${data.company_name}» endgültig gelöscht`);
      nav("/admin/clients");
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setDelBusy(false); }
  };
  const isSuper = user?.role === "super_admin";
  const readOnly = !canWriteClient(user, id);
  const usersReadOnly = !(isSuper || (!readOnly && user?.team_role === "admin_ops"));
  const activeIds = new Set(c.modules.filter((m) => m.active).map((m) => m.id));
  const openLeads = (c.leads || []).filter((l) => !["gewonnen", "verloren"].includes(l.status)).length;
  const openTasks = (c.tasks || []).filter((tk) => !["erledigt", "archiviert"].includes(tk.status)).length;
  const openInv = (c.invoices || []).filter((i) => ["offen", "überfällig"].includes(i.status));
  const openInvAmt = openInv.reduce((s, i) => s + (i.gross_chf || i.amount_chf || 0), 0);
  const nextAction = c.next_action_note || (openLeads > 0 ? "Anfrage beantworten" : openInvAmt > 0 ? "Rechnung prüfen" : openTasks > 0 ? "Aufgabe abschliessen" : "Alles erledigt");

  return (
    <div className="fade-in">
      {delConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="client-delete-modal">
          <div className="bg-[#121217] border border-red-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
              <h3 className="text-lg font-bold text-white">Kunde endgültig löschen</h3>
            </div>
            <p className="text-sm text-gray-400">Dies entfernt <span className="text-white font-semibold">{c.company_name}</span> und ALLE zugehörigen Daten (Website, Benutzer, Anfragen, Aufgaben, Rechnungen, Reports, Module, Support). Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <p className="text-xs text-gray-500 mt-3">Zur Bestätigung den exakten Firmennamen eingeben:</p>
            <input autoFocus value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder={c.company_name} data-testid="client-delete-confirm-input"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-400" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setDelConfirm(null)} data-testid="client-delete-cancel" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 rounded-lg text-sm">Abbrechen</button>
              <button onClick={deleteClient} disabled={delBusy || delConfirm.trim() !== c.company_name.trim()} data-testid="client-delete-confirm-btn"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                {delBusy ? "Lösche …" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("clients")}
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6" style={{ "--tenant": c.primary_color }}>
        <Avatar name={c.company_name} color={c.primary_color} src={c.logo_url || null} size={64} />
        <div className="flex-1">
          <h1 className="text-3xl font-black tracking-tighter">{c.company_name}</h1>
          <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
            <span>{c.branche}</span><span>·</span>
            <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#A3E635]">{c.domain} <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={c.status} />
          {c.client_type === "internal" && <span className="px-2 py-1 rounded-md bg-[#A3E635]/15 text-[#A3E635] text-xs font-bold uppercase tracking-wider" data-testid="client-type-badge">Intern / KEWAG</span>}
          {c.client_type === "test" && <span className="px-2 py-1 rounded-md bg-amber-500/15 text-amber-300 text-xs font-bold uppercase tracking-wider" data-testid="client-type-badge">Testsystem</span>}
          {c.pwa?.installed && (
            <span data-testid="client-pwa-badge" title={`${c.pwa.devices} ${c.pwa.devices === 1 ? "Gerät" : "Geräte"}${(c.pwa.platforms || []).length ? " · " + c.pwa.platforms.map((p) => ({ ios: "iOS", android: "Android", desktop: "Desktop" }[p] || p)).join(", ") : ""}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#A3E635]/12 text-[#A3E635] text-xs font-bold">
              <Smartphone className="w-3.5 h-3.5" />
              App installiert · {c.pwa.devices} {c.pwa.devices === 1 ? "Gerät" : "Geräte"}
              {c.pwa.last_active_at && <span className="text-[#A3E635]/70 font-semibold"> · zuletzt {new Date(c.pwa.last_active_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" })}</span>}
            </span>
          )}
          {isSuper && (
            <select value={c.client_type || "paying"} onChange={(e) => setClientType(e.target.value)} data-testid="client-type-select"
              className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-[#A3E635]">
              <option value="paying">Zahlender Kunde</option>
              <option value="internal">Internes KEWAG-System</option>
              <option value="test">Testsystem (QA)</option>
            </select>
          )}
          <button onClick={() => nav(`/admin/cockpit/${id}`)} data-testid="view-as-client"
            className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
            <Eye className="w-4 h-4" /> Als Kunde ansehen
          </button>
          {readOnly ? null : c.status === "archiviert" ? (
            <button onClick={() => setArchived(false)} data-testid="client-reactivate-btn"
              className="border border-[#A3E635]/40 text-[#A3E635] hover:bg-[#A3E635]/10 font-semibold py-2 px-3 rounded-lg text-sm flex items-center gap-2">
              <ArchiveRestore className="w-4 h-4" /> Reaktivieren
            </button>
          ) : (
            <button onClick={() => setArchived(true)} data-testid="client-archive-btn"
              className="border border-white/15 text-gray-300 hover:bg-white/5 font-semibold py-2 px-3 rounded-lg text-sm flex items-center gap-2">
              <Archive className="w-4 h-4" /> Archivieren
            </button>
          )}
          {isSuper && (
            <button onClick={() => setDelConfirm("")} data-testid="client-delete-btn" title="Kunde endgültig löschen"
              className="border border-red-500/30 text-red-300 hover:bg-red-500/10 font-semibold py-2 px-3 rounded-lg text-sm flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Löschen
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`tab-${tb}`}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === tb ? "border-[#A3E635] text-white" : "border-transparent text-gray-500 hover:text-white"}`}>
            {TAB_LABELS[tb]}
          </button>
        ))}
      </div>

      {/* 1 — Übersicht */}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="detail-statusrow">
            {[
              { label: "Offene Anfragen", value: openLeads, tab: "leads", tone: openLeads > 0 ? "text-amber-300" : "text-white", icon: Inbox },
              { label: "Offene Aufgaben", value: openTasks, tab: "aufgaben", tone: "text-white", icon: KanbanSquare },
              { label: "Offene Rechnungen", value: chf(openInvAmt), tab: "invoices", tone: openInvAmt > 0 ? "text-amber-300" : "text-white", icon: ReceiptText },
              { label: "Nächste Aktion", value: nextAction, tab: null, tone: "text-[#A3E635]", icon: ArrowRight, small: true },
            ].map((s, i) => (
              <button key={i} onClick={() => s.tab && setTab(s.tab)} data-testid={`detail-stat-${i}`}
                className="bg-[#121217] border border-white/10 rounded-xl p-4 text-left hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">{s.label}</span>
                  <s.icon className="w-4 h-4 text-gray-600" />
                </div>
                <div className={`mt-2 font-black tracking-tight ${s.tone} ${s.small ? "text-base leading-snug" : "text-2xl"}`}>{s.value}</div>
              </button>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 lg:col-span-2" data-testid="overview-snapshot">
              <h3 className="font-bold mb-4">Kunden-Snapshot</h3>
              <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                {[["Kontaktperson", c.contact_person], ["E-Mail", c.email], ["Branche", c.branche],
                  ["Paket", c.package?.name || "—"], ["Domain", c.domain], ["App-Domain", c.app_domain || "—"]].map(([k, v], i) => (
                  <div key={i} className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-500">{k}</span><span className="font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setTab("firma")} className="mt-4 text-xs font-semibold text-[#A3E635] hover:underline inline-flex items-center gap-1" data-testid="overview-to-firma">
                Firmendaten öffnen <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Card>
            <Card className="p-6">
              <h3 className="font-bold mb-4">Abo & Umsatz</h3>
              <div className="text-4xl font-black tracking-tighter">{chf(c.monthly_price || c.subscription?.price_chf)}<span className="text-sm text-gray-500 font-normal">/Mt. inkl. MWST</span></div>
              <div className="text-sm text-gray-500 mt-1">Verlängerung: {c.subscription?.renewal_date || "—"}</div>
              <div className="mt-4 flex items-center gap-2"><Globe className="w-4 h-4 text-gray-500" /><StatusBadge status={c.website?.status} /></div>
              <div className="mt-2 text-xs text-gray-500">Kunden-Cockpit: <span className="text-[#A3E635] font-semibold">Aktiv</span></div>
              <button onClick={() => setTab("abo")} className="mt-4 text-xs font-semibold text-[#A3E635] hover:underline inline-flex items-center gap-1" data-testid="overview-to-abo">
                Abo & Vertrag öffnen <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Card>
          </div>
        </div>
      )}

      {/* 2 — Firmendaten */}
      {tab === "firma" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <StammdatenCard client={c} packages={packages} onSaved={load} readOnly={readOnly} />
        </div>
      )}

      {/* 3 — Kontaktpersonen */}
      {tab === "kontakte" && (
        <div className="space-y-6" data-testid="detail-kontakte">
          <ContactsTab clientId={id} readOnly={readOnly} />
          <ClientUserAccess clientId={id} readOnly={usersReadOnly} />
        </div>
      )}

      {tab === "users" && <ClientUserAccess clientId={id} readOnly={usersReadOnly} />}

      {/* 4 — Website & Technik */}
      {tab === "website" && (
        <div className="space-y-6">
          <WebsiteConnEditor website={c.website} clientId={id} onChange={load} onCheck={checkSite} checking={checking} readOnly={readOnly} />
          <SpecialAppEditor client={c} onSaved={load} readOnly={readOnly} />
        </div>
      )}

      {/* 5 — Abo & Vertrag */}
      {tab === "abo" && (
        <div className="space-y-6" data-testid="detail-abo">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6">
              <h3 className="font-bold mb-4">Abo & Umsatz</h3>
              <div className="text-4xl font-black tracking-tighter">{chf(c.monthly_price || c.subscription?.price_chf)}<span className="text-sm text-gray-500 font-normal">/Mt. inkl. MWST</span></div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Paket</span><span className="font-medium">{c.package?.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Setup-Gebühr</span><span className="font-medium">{chf(c.setup_fee)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Laufzeit</span><span className="font-medium">{c.contract_months} Monate</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Vertragsstart</span><span className="font-medium">{c.contract_start || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Verlängerung</span><span className="font-medium">{c.subscription?.renewal_date || "—"}</span></div>
              </div>
              {!readOnly && <button onClick={() => setTab("firma")} className="mt-4 text-xs font-semibold text-[#A3E635] hover:underline inline-flex items-center gap-1" data-testid="abo-edit-link">
                <Pencil className="w-3.5 h-3.5" /> Preise & Laufzeit bearbeiten (Firmendaten)
              </button>}
            </Card>
            <Card className="p-6 lg:col-span-2">
              <h3 className="font-bold mb-4">Offene Rechnungen</h3>
              {openInv.length === 0 ? <Empty text="Keine offenen Rechnungen" /> : (
                <div className="divide-y divide-white/5">
                  {openInv.map((i) => (
                    <div key={i.id} className="py-3 flex items-center justify-between gap-3">
                      <div><div className="font-medium text-sm">{i.number}</div><div className="text-xs text-gray-500">{i.period}</div></div>
                      <div className="flex items-center gap-4"><span className="font-bold">{chf(i.gross_chf || i.amount_chf)}</span><StatusBadge status={i.status} /></div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setTab("invoices")} className="mt-4 text-xs font-semibold text-[#A3E635] hover:underline inline-flex items-center gap-1">
                Alle Rechnungen <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Card>
          </div>
          <div className="space-y-6">
            <h3 className="font-bold">Module & Booster</h3>
            {[["basis", "Basic-Module"], ["modul", "Zusatz-Module"], ["booster", "Booster"]].map(([cat, label]) => (
              <div key={cat}>
                <p className="text-xs font-bold text-gray-300 mb-2 uppercase tracking-wider">{label}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allModules.filter((m) => m.category === cat).map((m) => {
                    const on = activeIds.has(m.id);
                    return (
                      <div key={m.id} className={`p-4 rounded-xl border ${on ? "border-[#A3E635]/40 bg-[#A3E635]/5" : "border-white/10"}`}>
                        <div className="flex items-start justify-between">
                          <div><div className="font-semibold text-sm">{m.name}</div><div className="text-[11px] text-gray-500">{m.price_chf ? `CHF ${m.price_chf}` : "Inklusive"}</div></div>
                          <button onClick={() => toggleModule(m.id, !on)} disabled={readOnly} data-testid={`detail-mod-${m.key}`}
                            className={`relative w-11 h-6 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${on ? "bg-[#A3E635]" : "bg-white/10"}`}>
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{m.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6 — Aufgaben & Änderungen */}
      {tab === "aufgaben" && (
        <div className="space-y-6" data-testid="detail-aufgaben">
          <div>
            <h3 className="font-bold mb-3">Aufgaben</h3>
            <Card className="divide-y divide-white/5" data-testid="detail-tasks">
              {c.tasks.length === 0 ? <Empty text={t("noData")} /> : c.tasks.map((tk) => (
                <button key={tk.id} onClick={() => nav(`/admin/tasks/${tk.id}`)} disabled={user?.role !== "super_admin" && !(user?.admin_sections || []).includes("tasks")} data-testid={`detail-task-${tk.id}`}
                  className="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-white/[0.02] transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {tk.title}
                      {tk.kunde_am_zug && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300">Kunde am Zug</span>}
                      {tk.overdue && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-300">Überfällig</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {tk.assignee} · {tk.visibility_label}{tk.next_action ? ` · Nächste Aktion: ${tk.next_action}` : ""}
                      {tk.progress?.total > 0 ? ` · ${tk.progress.done}/${tk.progress.total} erledigt` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs ${tk.overdue ? "text-red-300 font-bold" : "text-gray-400"}`}>{(tk.due_date || "").slice(0, 10) || "—"}</span>
                    <StatusBadge status={tk.status} />
                  </div>
                </button>
              ))}
            </Card>
          </div>
          <div>
            <h3 className="font-bold mb-3">Nächste Schritte & Änderungswünsche</h3>
            <div className="space-y-3">
              {(c.next_steps || []).length === 0 ? <Empty text={t("noData")} /> : c.next_steps.map((s) => (
                <Card key={s.id} className="p-5 flex items-start gap-4">
                  <div className="w-1 self-stretch rounded-full bg-[#A3E635]" />
                  <div><div className="font-semibold">{s.title}</div><div className="text-sm text-gray-400 mt-1">{s.description}</div></div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 7 — Anfragen / Leads */}
      {tab === "leads" && (
        <Card className="divide-y divide-white/5">
          {c.leads.length === 0 ? <Empty text={t("noData")} /> : c.leads.map((l) => (
            <div key={l.id} className="p-4 flex items-center justify-between gap-3">
              <div><div className="font-medium text-sm">{l.name}</div><div className="text-xs text-gray-500">{l.email} · {l.message}</div></div>
              <StatusBadge status={l.status} />
            </div>
          ))}
        </Card>
      )}

      {/* 8 — Bewertungen */}
      {tab === "bewertungen" && (
        <ReviewsTab clientId={id} client={c} readOnly={readOnly} />
      )}

      {/* 9 — Design- & Growth-Bibliothek */}
      {tab === "design" && (
        <DesignLibraryTab clientId={id} client={c} readOnly={readOnly} />
      )}

      {/* 10 — Monatsreport */}
      {tab === "reports" && (
        <div className="space-y-4" data-testid="detail-reports">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-bold">Monatsreports</h3>
            <button onClick={() => nav(`/admin/reports`)} data-testid="reports-manage-link"
              className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Report-Verwaltung öffnen
            </button>
          </div>
          {(!c.reports || c.reports.length === 0) ? (
            <Empty text="Noch keine Monatsreports vorhanden. In der Report-Verwaltung kann KEWAG den ersten Report erstellen." />
          ) : (
            <Card className="divide-y divide-white/5">
              {c.reports.map((r) => (
                <div key={r.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{r.month_label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.analytics_connected ? `${r.visits} Besuche · ` : ""}{r.leads_count} Anfragen · {r.completed_tasks} Aufgaben erledigt
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${r.id}/pdf`} target="_blank" rel="noreferrer"
                      className="text-xs font-semibold text-gray-300 hover:text-white bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-testid={`report-pdf-${r.id}`}>
                      <ReceiptText className="w-3.5 h-3.5" /> PDF
                    </a>
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* 11 — Rechnungen */}
      {tab === "invoices" && (
        <Card className="divide-y divide-white/5">
          {c.invoices.length === 0 ? <Empty text={t("noData")} /> : c.invoices.map((i) => (
            <div key={i.id} className="p-4 flex items-center justify-between gap-3">
              <div><div className="font-medium text-sm">{i.number}</div><div className="text-xs text-gray-500">{i.period}</div></div>
              <div className="flex items-center gap-4">
                <span className="font-bold">{chf(i.amount_chf)}</span>
                <StatusBadge status={i.status} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* 12 — Dokumente */}
      {tab === "documents" && (
        <DocumentsManager clientId={id} readOnly={readOnly} />
      )}

      {/* 13 — Audit-Log */}
      {tab === "audit" && (
        <Card className="divide-y divide-white/5" data-testid="detail-audit">
          {(!c.activity || c.activity.length === 0) ? <Empty text={t("noData")} /> : c.activity.map((a, i) => (
            <div key={a.id || i} className="p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                <ScrollText className="w-4 h-4 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{a.action}</span>
                  <span className="text-[11px] text-gray-600 shrink-0 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{a.actor_name || a.actor_email || "System"}{a.entity_type ? ` · ${a.entity_type}` : ""}</div>
                {a.changes && (
                  <div className="mt-2 text-[11px] text-gray-400 bg-white/[0.03] rounded-lg p-2 space-y-0.5">
                    {Object.entries(a.changes).map(([k, v]) => (
                      <div key={k}><span className="text-gray-500">{k}:</span> {String(v?.von ?? "—")} → <span className="text-white">{String(v?.zu ?? "—")}</span></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

